import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { ClickAwayListener, Grow, Popper } from '@material-ui/core'
import ScrollContent from '../../../molecules/ScrollContent'
import SydButton from '../../../commonDesign/Button'
import EmptySection from '../../../atoms/EmptySection'
import FadeIn from '../../../molecules/Transitions/FadeIn'
import { KEYS } from '../../../../constants'
import InlineSearchInput from './InlineSearchInput'
import SelectMenuOption from './SelectMenuOption'

function MultiPopoverMenu ({
  anchorEl,
  open,
  setOpen,
  options,
  filteredOptions,
  pinnedOptions,
  filteredPinnedOptions,
  onSearchChange,
  onSelect,
  defaultFilterValues
}) {
  const [_values, setValues] = useState(defaultFilterValues)
  const values = useMemo(() => {
    return _values.map(v => options.find(o => o.attributes.id === v)).filter(Boolean)
  }, [options, _values])
  const handleAdd = useCallback((item) => {
    setValues(prev => {
      const nv = [...prev]
      if (nv.length === 10) return nv
      nv.push(item.attributes.id)
      return nv
    })
  }, [setValues])
  const handleRemove = useCallback((item) => {
    setValues(prev => {
      const nv = [...(prev || []).filter(x => x !== item.attributes.id)]
      return nv
    })
  }, [setValues])
  const handleClear = useCallback(() => {
    setValues([])
  }, [])
  const helpMessage = useMemo(() => {
    if (values?.length === 10) {
      return 'Maximum Limit 10 Filter Items'
    }
    return ''
  }, [values])

  const resetValues = useCallback(() => {
    setValues(defaultFilterValues)
  }, [setValues, defaultFilterValues])
  useLayoutEffect(() => {
    resetValues()
  }, [open, resetValues])

  const handleEnter = useCallback((e) => {
    if (open && e.which === KEYS.ENTER) {
      onSelect(values)
      e.stopPropagation()
      e.preventDefault()
    }
  }, [open, onSelect, values])

  const handleEscape = useCallback((e) => {
    if (open && e.which === KEYS.ESC) {
      setOpen(false)
      e.stopPropagation()
      e.preventDefault()
    }
  }, [open, setOpen])

  // hotkey handlers
  useEffect(() => {
    const handler = (e) => {
      handleEscape(e)
      handleEnter(e)
    }
    window.addEventListener('keydown', handler, false)
    return () => {
      window.removeEventListener('keydown', handler, false)
    }
  }, [handleEscape, handleEnter])

  const handleClose = useCallback(() => setOpen(false), [setOpen])
  const animateMenu = useCallback(({ TransitionProps, placement }) => {
    return (
      <Grow {...TransitionProps} style={{ transformOrigin: 'top' }}>
        <div className='opal-drop-menu below select-none'>
          <ClickAwayListener onClickAway={handleClose}>
            <div>
              <div className='flex flex-row'>
                <div className='flex flex-col' style={{ maxWidth: '400px', width: '400px' }}>
                  <ScrollContent maxHeight={500}>
                    <div>
                      <InlineSearchInput onChange={onSearchChange} />
                    </div>
                    {(filteredPinnedOptions || []).length ? (
                      <>
                        {pinnedOptions.map(option => (
                          <SelectMenuOption key={`pi_${option.value}`} option={option} onClick={onSelect} />
                        ))}
                        <div className='divider' />
                      </>
                    ) : null}
                    {(filteredOptions || []).map(option => {
                      const selected = _values.includes(option?.attributes?.id)
                      return (
                        <SelectMenuOption key={`mi_${option.value}`} option={option} onClick={handleAdd} onDoubleClick={onSelect} disabled={selected} />
                      )
                    })}
                    {((filteredPinnedOptions || []).length + (filteredOptions || []).length) === 0 ? (
                      <div className='p-sm flex justify-center font-size-sm'>
                        No results
                      </div>
                    ) : null}
                  </ScrollContent>
                </div>
                <div className='flex flex-col' style={{ maxWidth: '400px', width: '400px', borderLeft: 'var(--border-neutral-thin)' }}>
                  <ScrollContent maxHeight={500}>
                    {(values || []).length === 0 ? (
                      <div className='pt-20'>
                        <EmptySection title='No Filter' description='Select Items to filter' />
                      </div>
                    ) : (
                      <>
                        {helpMessage ? (
                          <FadeIn className='flex flex-row justify-center p-2 bg-warning-500'>{helpMessage}</FadeIn>
                        ) : (
                          <div className='flex flex-row justify-center p-2'>{values.length} Selected Item(s)</div>
                        )}
                        {values?.map(v => (
                          <SelectMenuOption key={`sv_${v.value}`} option={v} onClick={handleRemove} />
                        ))}
                      </>
                    )}
                  </ScrollContent>
                </div>
              </div>
              <div className='flex flex-row bg-neutral-200'>
                <SydButton variant='ghost' className='flex-auto' style={{ width: '400px', borderRadius: 0 }} onClick={handleClear}>Clear</SydButton>
                <SydButton variant='primary' className='flex-auto' style={{ width: '398px', borderRadius: 0 }} onClick={() => onSelect(values)}>Apply</SydButton>
              </div>
            </div>
          </ClickAwayListener>
        </div>
      </Grow>
    )
  }, [handleClose, onSelect, onSearchChange, pinnedOptions, handleAdd, handleRemove, handleClear, values, _values, helpMessage, filteredOptions, filteredPinnedOptions])

  return (
    <Popper
      transition
      open={open}
      placement='bottom-start'
      className='z-index-dropdown'
      anchorEl={anchorEl}
    >
      {animateMenu}
    </Popper>
  )
}

MultiPopoverMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool,
  setOpen: PropTypes.func,
  options: PropTypes.array,
  filteredOptions: PropTypes.array,
  pinnedOptions: PropTypes.array,
  filteredPinnedOptions: PropTypes.array,
  onSearchChange: PropTypes.func,
  onSelect: PropTypes.func,
  defaultFilterValues: PropTypes.arrayOf(PropTypes.number)
}

export default MultiPopoverMenu
